import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useCallback, useEffect, useState } from "react";
import { dateLocale } from "@/lib/dateTime";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { InfiniteScroll } from "@/components/ui/infinite-scroll";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useWhatsAppUpdates } from "@/lib/useWhatsAppUpdates";
import { cn } from "@/lib/utils";
import { ChatPane, formatTime } from "@/components/whatsapp/ChatPane";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { PageHeader, ViewSwitch, initialsOf } from "@/components/patterns";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Ban, CheckCircle2, Info, Loader2, LockKeyhole, RotateCcw, Search, UserPlus, X } from "lucide-react";
import { QuickRepliesManager } from "./QuickRepliesManager";
import { WhatsAppReports } from "./WhatsAppReports";
import { CampaignsTab } from "./CampaignsTab";
import { dateRangeParams } from "@/lib/filters";
import type { WaConversation, WaConversationDetail } from "@/types/whatsapp";

type View = "all" | "mine" | "unread" | "waiting" | "unassigned" | "closed";
type Counts = Record<View, number>;
type Today = { first_reply_minutes: number | null; chats_handled: number; templates_sent: number };

/** List time: "16:05" today, "Yesterday", weekday this week, else date */
const listTime = (v: string | null) => {
  if (!v) return "";
  const d = new Date(v);
  if (d.toDateString() === new Date().toDateString()) return formatTime(v);
  if (d.toDateString() === new Date(Date.now() - 86400000).toDateString()) return "Yesterday";
  if (Date.now() - d.getTime() < 6 * 86400000) return d.toLocaleDateString(dateLocale(), { weekday: "short" });
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
};

/** "1 h 12 min", "35 min", "2 days" */
const span = (ms: number) => {
  const min = Math.max(1, Math.round(ms / 60000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${min % 60 ? `${min % 60} min` : ""}`.trim();
  return `${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? "" : "s"}`;
};

const Avatar = ({ name, size = 34, tone = "bg-accent text-accent-foreground" }: { name?: string | null; size?: number; tone?: string }) => (
  <span className={cn("flex shrink-0 items-center justify-center rounded-full font-bold", tone)} style={{ width: size, height: size, fontSize: size * 0.32 }} aria-hidden="true">
    {initialsOf(name)}
  </span>
);

export function WhatsAppPage() {
  const { hasPermission, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "inbox";
  const selectedId = params.get("c");
  const isAdmin = hasPermission("whatsapp.admin");
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();

  const [view, setView] = useState<View>("all");
  const [activity, setActivity] = useState("any");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [list, setList] = useState<WaConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listPageSize, setListPageSize] = useState(30);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState<Partial<Counts>>({});
  const [today, setToday] = useState<Today | null>(null);

  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("c", id);
    else next.delete("c");
    setParams(next, { replace: true });
  };
  const setTab = (t: string) => setParams(t === "inbox" ? {} : { tab: t }, { replace: true });

  const fetchChats = useCallback(
    (pageToLoad: number, append: boolean) => {
      const qs = new URLSearchParams({ view, page: String(pageToLoad), ...dateRangeParams(activity, "date") });
      if (query) qs.set("search", query);
      return api.get(`/api/WhatsApp/Conversations?${qs}`).then((res) => {
        const paged = unwrapPage<WaConversation>(res);
        setList((prev) => {
          if (!append) return paged.rows;
          const ids = new Set(prev.map((c) => c.id));
          return [...prev, ...paged.rows.filter((c) => !ids.has(c.id))];
        });
        setListTotal(paged.total);
        setListPage(paged.page);
        setListPageSize(paged.page_size);
      });
    },
    [view, query, activity],
  );

  const loadCounts = useCallback(() => {
    api.get("/api/WhatsApp/Counts").then((r) => setCounts(unwrapObject(r))).catch(() => undefined);
    api.get("/api/WhatsApp/TodayStats").then((r) => setToday(unwrapObject(r))).catch(() => undefined);
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    fetchChats(1, false)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
    loadCounts();
  }, [fetchChats, loadCounts]);

  const loadMoreChats = () => {
    if (loadingMore || listPage * listPageSize >= listTotal) return;
    setLoadingMore(true);
    fetchChats(listPage + 1, true)
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/api/WhatsApp/Settings")
      .then((res) => setConnected(Boolean(unwrapObject<any>(res)?.connected)))
      .catch(() => undefined);
  }, [isAdmin]);

  useWhatsAppUpdates((e) => {
    loadList();
    if (!e || e.conversation_id === selectedId) setRefresh((n) => n + 1);
  }, tab === "inbox");

  const views: { key: View; label: string; tone?: string }[] = [
    { key: "all", label: roleCode === "SALES_HEAD" ? "Team" : "All" },
    { key: "mine", label: "Mine" },
    { key: "unread", label: "Unread" },
    { key: "waiting", label: "Waiting 1h+", tone: "warning" },
    { key: "unassigned", label: "Unassigned" },
    { key: "closed", label: "Closed" },
  ];

  const tabs = [
    { key: "inbox", label: "Chats" },
    ...(hasPermission("whatsapp.send") ? [{ key: "quick", label: "Quick replies" }] : []),
    ...(hasPermission("whatsapp.campaign") ? [{ key: "campaigns", label: "Campaigns" }] : []),
    { key: "reports", label: "Reports" },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <PageHeader
        title="WhatsApp"
        switcher={tabs.length > 1 ? <ViewSwitch value={tab === "settings" ? "inbox" : tab} options={tabs} onChange={setTab} label="WhatsApp sections" /> : undefined}
        actions={
          isAdmin ? (
            <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
              <Link to="/settings/whatsapp">Templates & settings</Link>
            </Button>
          ) : undefined
        }
      />

      {tab === "settings" && <Navigate to="/settings/whatsapp" replace />}
      {tab === "quick" && <QuickRepliesManager />}
      {tab === "campaigns" && <CampaignsTab />}
      {tab === "reports" && <WhatsAppReports />}

      {tab === "inbox" && (
        <>
          {connected === false && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
              <span>The company WhatsApp number isn't connected yet.</span>
              <Button size="sm" onClick={() => navigate("/settings/whatsapp")}>
                Connect WhatsApp
              </Button>
            </div>
          )}

          <div className="wa flex min-h-[600px] flex-1 overflow-hidden rounded-lg border border-border bg-card">
            {/* Conversations */}
            <aside className={cn("min-h-0 w-full shrink-0 flex-col border-r border-border bg-panel md:w-[320px] lg:w-[340px]", selectedId ? "hidden md:flex" : "flex")}>
              <div className="flex flex-col gap-2 border-b border-border p-3">
                <label className="flex h-[30px] items-center gap-2 rounded-lg border border-border bg-card px-2.5 text-muted-foreground">
                  <Search className="size-3.5 shrink-0" />
                  <span className="sr-only">Search chats</span>
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, number or deal" className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground" />
                  {search && (
                    <button type="button" onClick={() => setSearch("")} aria-label="Clear search">
                      <X className="size-3.5" />
                    </button>
                  )}
                </label>
                <div role="tablist" aria-label="Chat filter" className="flex flex-wrap gap-1">
                  {views.map((v) => {
                    const on = view === v.key;
                    const n = counts[v.key];
                    return (
                      <button
                        key={v.key}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => setView(v.key)}
                        className={cn(
                          "h-[26px] whitespace-nowrap rounded-full px-2.5 text-[11.5px] transition-colors",
                          on ? "bg-sidebar font-semibold text-white" : v.tone === "warning" && n ? "border border-warning/40 bg-warning-soft font-semibold text-warning" : "border border-border bg-card text-foreground hover:bg-muted",
                        )}
                      >
                        {v.label}
                        {n !== undefined && n > 0 && v.key !== "all" ? ` · ${n}` : ""}
                      </button>
                    );
                  })}
                </div>
                <SearchableSelect value={activity} onChange={(e) => setActivity(e.target.value)} aria-label="Last activity" className="h-7 rounded-md border border-border bg-card px-2 text-[11.5px] text-muted-foreground outline-none">
                  <option value="any">Last activity: any time</option>
                  <option value="today">Last activity: today</option>
                  <option value="last_7">Last activity: last 7 days</option>
                  <option value="this_month">Last activity: this month</option>
                  <option value="last_30">Last activity: last 30 days</option>
                </SearchableSelect>
              </div>

              <div className="wa-scroll min-h-0 flex-1 overflow-y-auto">
                {loading && list.length === 0 ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="size-5 animate-spin text-primary" />
                  </div>
                ) : list.length === 0 ? (
                  <div className="px-6 py-12 text-center text-[13px] text-muted-foreground">{view === "unread" ? "No unread chats" : view === "waiting" ? "Nobody is waiting on you" : "No chats here"}</div>
                ) : (
                  list.map((c) => {
                    const active = selectedId === c.id;
                    const waiting = !!c.waiting_since && Date.now() - new Date(c.waiting_since).getTime() > 3600000;
                    const name = c.profile_name || c.phone;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => select(c.id)}
                        className={cn("flex w-full gap-2.5 border-b border-border/70 border-l-[3px] px-3 py-2.5 text-left transition-colors hover:bg-muted/60", active ? "border-l-success bg-card" : "border-l-transparent")}
                      >
                        <Avatar name={name} tone={c.contact_id || c.lead_id ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"} />
                        <span className="flex min-w-0 flex-1 flex-col gap-px">
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-[13.5px] font-semibold text-foreground">{name}</span>
                            <span className={cn("shrink-0 font-mono text-[11px]", waiting ? "font-semibold text-warning" : c.unread_count ? "font-semibold text-success" : "text-muted-foreground")}>{listTime(c.last_message_at)}</span>
                          </span>
                          <span className="truncate text-[11.5px] text-muted-foreground">
                            {[c.account_name, c.deal?.name].filter(Boolean).join(" · ") || (c.contact_id || c.lead_id ? "In the CRM" : "New number")}
                            {c.conversation_status === "CLOSED" ? " · Closed" : ""}
                          </span>
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate text-xs text-foreground/80">{c.last_message_preview || c.phone}</span>
                            {c.unread_count > 0 && <span className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-success px-1 text-[10px] font-bold text-white">{c.unread_count}</span>}
                          </span>
                          <span className="text-[11px] text-muted-foreground">{c.assigned_user ? c.assigned_user.name : <span className="text-warning">Unassigned</span>}</span>
                        </span>
                      </button>
                    );
                  })
                )}
                {list.length > 0 && <InfiniteScroll hasMore={listPage * listPageSize < listTotal} loading={loadingMore} onLoad={loadMoreChats} />}
              </div>
            </aside>

            {/* Chat */}
            {selectedId ? (
              <ConversationView key={selectedId} conversationId={selectedId} today={today} refreshSignal={refresh} onBack={() => select(null)} onChanged={loadList} />
            ) : (
              <div className="hidden flex-1 flex-col items-center justify-center border-b-[6px] border-[#25D366] bg-muted/40 px-10 text-center md:flex">
                <WhatsAppIcon className="mb-5 size-16" color="#25D366" />
                <h2 className="text-2xl font-semibold tracking-tight text-foreground">Pick a chat</h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">Messages from the company number land here, linked to the contact, lead and deal automatically. Chats waiting more than an hour are marked in amber.</p>
                <p className="mt-8 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <LockKeyhole className="size-3.5" /> Official WhatsApp Business
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Chat header, the chat, and the CRM side panel */
function ConversationView({
  conversationId,
  today,
  refreshSignal,
  onBack,
  onChanged,
}: {
  conversationId: string;
  today: Today | null;
  refreshSignal: number;
  onBack: () => void;
  onChanged: () => void;
}) {
  const { hasPermission, user } = useAuth();
  const { success, error } = useToast();
  const [detail, setDetail] = useState<WaConversationDetail | null>(null);
  const [users, setUsers] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1280);
  const [, tick] = useState(0);
  const canSend = hasPermission("whatsapp.send");
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();
  const supervisor = roleCode === "SUPER_ADMIN" || roleCode === "SALES_HEAD";
  const myId = user?.Id;

  const load = useCallback(() => {
    api
      .get(`/api/WhatsApp/Conversations/${conversationId}`)
      .then((res) => setDetail(unwrapObject<WaConversationDetail>(res)))
      .catch(() => setDetail(null));
  }, [conversationId]);
  useEffect(load, [load, refreshSignal]);

  // Keep "waiting for 1 h 12 min" fresh
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!supervisor) return;
    api
      .get("/api/User/Assignable")
      .then((res) => setUsers(unwrapArray(res)))
      .catch(() => setUsers([]));
  }, [supervisor]);

  const act = async (fn: () => Promise<any>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) success(ok);
      load();
      onChanged();
    } catch (err: any) {
      error("Action failed", err.message);
    } finally {
      setBusy(false);
    }
  };

  const conv = detail?.conversation;
  const name = conv?.profile_name || conv?.phone || "…";
  const company = detail?.contact?.account?.name || detail?.lead?.company_name;
  const waitingMs = conv?.waiting_since ? Date.now() - new Date(conv.waiting_since).getTime() : 0;
  const windowMs = conv?.window_closes_at ? new Date(conv.window_closes_at).getTime() - Date.now() : 0;
  const saved = !!(detail?.contact || detail?.lead);

  return (
    <>
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-[56px] shrink-0 flex-wrap items-center gap-2.5 border-b border-border bg-card px-4 py-2">
          <button type="button" className="-ml-1 md:hidden" onClick={onBack} aria-label="Back">
            <ArrowLeft className="size-5 text-muted-foreground" />
          </button>
          <Avatar name={name} />
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-sm font-semibold">{name}</span>
            <span className="truncate text-[11.5px] text-muted-foreground">{[conv?.phone, company].filter(Boolean).join(" · ")}</span>
          </span>
          {conv?.opted_out && (
            <span className="flex items-center gap-1 rounded-full bg-danger px-2 py-0.5 text-[11px] text-white">
              <Ban className="size-3" /> Opted out
            </span>
          )}
          {conv && canSend && !conv.assigned_user_id && myId && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.patch(`/api/WhatsApp/Conversations/${conversationId}/Assign`, { user_id: myId }), "Assigned to you")}>
              Assign to me
            </Button>
          )}
          {conv && canSend && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => act(() => api.patch(`/api/WhatsApp/Conversations/${conversationId}/${conv.conversation_status === "OPEN" ? "Close" : "Reopen"}`), conv.conversation_status === "OPEN" ? "Marked resolved" : "Conversation reopened")}
            >
              {conv.conversation_status === "OPEN" ? (
                <>
                  <CheckCircle2 className="mr-1.5 size-3.5" /> Mark resolved
                </>
              ) : (
                <>
                  <RotateCcw className="mr-1.5 size-3.5" /> Reopen
                </>
              )}
            </Button>
          )}
          <Button size="icon-sm" variant="ghost" title="Details" aria-pressed={infoOpen} aria-label="Details" onClick={() => setInfoOpen((v) => !v)} className={infoOpen ? "text-primary" : "text-muted-foreground"}>
            <Info className="size-4" />
          </Button>
        </header>

        {conv?.waiting_since && waitingMs > 3600000 && (
          <div role="status" className="shrink-0 border-b border-warning/30 bg-warning-soft px-4 py-1.5 text-center text-xs text-warning">
            Waiting on us for <strong>{span(waitingMs)}</strong>
            {windowMs > 0 ? (
              <> · free-reply window closes in <strong>{span(windowMs)}</strong></>
            ) : (
              <> · the free-reply window has closed, so only a template can be sent</>
            )}
          </div>
        )}

        <ChatPane conversationId={conversationId} refreshSignal={refreshSignal} onChanged={() => (load(), onChanged())} className="flex-1" />
      </section>

      {/* CRM panel */}
      {infoOpen && (
        <aside className="hidden w-[280px] shrink-0 flex-col gap-3 overflow-y-auto border-l border-border bg-card p-3.5 lg:flex">
          {!detail || !conv ? (
            <Loader2 className="mx-auto mt-10 size-5 animate-spin text-primary" />
          ) : (
            <>
              <Section title="Linked in the CRM">
                {detail.contact && (
                  <Link to={`/contacts/${detail.contact.id}`} className="text-[13px] font-semibold text-primary hover:underline">
                    {detail.contact.name} <span className="font-normal text-muted-foreground">· contact{detail.contact.job_title ? `, ${detail.contact.job_title}` : ""}</span>
                  </Link>
                )}
                {detail.contact?.account && (
                  <Link to={`/accounts/${detail.contact.account.id}`} className="text-[13px] text-primary hover:underline">
                    {detail.contact.account.name} <span className="text-muted-foreground">· account</span>
                  </Link>
                )}
                {!detail.contact && detail.lead && (
                  <Link to={`/leads/${detail.lead.id}`} className="text-[13px] font-semibold text-primary hover:underline">
                    {detail.lead.name} <span className="font-normal text-muted-foreground">· lead{detail.lead.company_name ? `, ${detail.lead.company_name}` : ""}</span>
                  </Link>
                )}
                {conv.deal && (
                  <Link to={`/deals/${conv.deal.id}`} className="text-[13px] text-primary hover:underline">
                    {conv.deal.name} <span className="text-muted-foreground">· deal</span>
                  </Link>
                )}
                {canSend && detail.deals.length > 0 && (
                  <Select value={conv.deal_id || "none"} onValueChange={(v) => act(() => api.patch(`/api/WhatsApp/Conversations/${conversationId}/Link`, { deal_id: v === "none" ? null : v }), "Deal link updated")}>
                    <SelectTrigger className="mt-1 h-8 w-full text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No deal</SelectItem>
                      {detail.deals.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name} {d.deal_status !== "OPEN" ? `(${d.deal_status.toLowerCase()})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {!saved && <span className="text-xs text-muted-foreground">This number isn't saved in the CRM yet.</span>}
                <span className="text-[11.5px] text-muted-foreground">Messages here also show on the contact, account and deal timelines.</span>
              </Section>

              <Section title="Do from this chat">
                {hasPermission("activity.create") && (
                  <button type="button" onClick={() => setTaskOpen(true)} className="h-[30px] rounded-md border border-border px-2.5 text-left text-xs hover:bg-muted">
                    Add follow-up task
                  </button>
                )}
                {conv.deal && (
                  <Link to={`/deals/${conv.deal.id}`} className="flex h-[30px] items-center rounded-md border border-border px-2.5 text-xs hover:bg-muted">
                    Open the deal
                  </Link>
                )}
              </Section>

              {!saved && canSend && hasPermission("lead.create") && (
                <Section title="Unknown number?">
                  <span className="text-xs text-foreground/80">Make a lead from this chat and it will be linked to the number.</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => act(() => api.post(`/api/WhatsApp/Conversations/${conversationId}/CreateLead`), "Lead created")}
                    className="flex h-[30px] items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/50 text-xs font-medium text-primary hover:bg-accent/50"
                  >
                    <UserPlus className="size-3.5" /> Create lead from chat
                  </button>
                </Section>
              )}

              <Section title="Assigned to">
                {supervisor && canSend ? (
                  <Select value={conv.assigned_user_id || "none"} onValueChange={(v) => act(() => api.patch(`/api/WhatsApp/Conversations/${conversationId}/Assign`, { user_id: v === "none" ? null : v }), "Assignment updated")}>
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name || ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[13px]">{conv.assigned_user?.name || "Unassigned"}</span>
                )}
              </Section>

              {conv.opted_out && (
                <Section title="Messaging">
                  <p className="flex items-center gap-1.5 text-xs text-danger">
                    <Ban className="size-4" /> Customer replied STOP. Templates are blocked.
                  </p>
                </Section>
              )}

              {today && (
                <Section title="Your day">
                  {[
                    ["First reply time", today.first_reply_minutes === null ? "—" : `${today.first_reply_minutes} min`],
                    ["Chats answered", String(today.chats_handled)],
                    ["Templates sent", String(today.templates_sent)],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between text-xs">
                      <span className="text-foreground/80">{label}</span>
                      <span className="font-mono font-semibold">{value}</span>
                    </div>
                  ))}
                </Section>
              )}
            </>
          )}
        </aside>
      )}

      <TaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        defaultRelated={detail?.contact ? { type: "CONTACT", id: detail.contact.id, name: detail.contact.name } : detail?.lead ? { type: "LEAD", id: detail.lead.id, name: detail.lead.name } : undefined}
        onSaved={() => setTaskOpen(false)}
      />
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5 border-t border-border/70 pt-3 first:border-t-0 first:pt-0">
      <h3 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

export default WhatsAppPage;
